import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { paper } from 'common/theme/colors';
import { getMainNavConfig, getVideoNavConfig, NavItem } from 'common/constants/nav';
import { theme } from 'common/theme/theme';
import { useMenuWidth } from 'common/hooks/use-menu-width';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Styles } from 'common/types/styles';
import { userService } from 'common/services/user.service';
import { RetailerSelect } from 'common/ui/inputs/retailer/retailer-select';
import { AdvonLogoIcon } from 'common/svg/advon-logo-icon';
import { DemoBlurWrapper } from 'common/ui/containers/demo-blur-wrapper';
import { useVideoAppContext } from 'common/ui/containers/video-app-context';
import { LightTooltip } from 'common/ui/containers/light-tooltip';
import { useDemoContext } from 'common/ui/containers/demo-context';
import CategoryIcon from '@mui/icons-material/Category';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { useMeQuery } from 'common/hooks/api/queries/use-users-query';
import { useCurrrentRoute } from 'common/hooks/use-current-route';
import { TenantSelect } from 'common/ui/inputs/retailer/tenant-select';
import { NavItems } from './nav-items';

interface StylesProps {
  menuWidth: number;
  withRetailer: boolean;
}

const getStyles = ({ menuWidth, withRetailer }: StylesProps): Styles => ({
  menuContainer: {
    width: menuWidth,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.primary.dark,
    overflowY: 'scroll',
    scrollbarWidth: 'none',
    padding: theme.spacing(3.5, 0, 5),
  },
  brand: { alignSelf: 'center', color: theme.palette.background.paper, fontWeight: 700 },
  navItemsWrapper: { marginTop: withRetailer ? '50px' : '90px' },
  drawer: { border: 'none' },
  logo: { color: paper, fontSize: 16, fontWeight: 400, letterSpacing: 1, marginLeft: 0.5 },
  greyedLogo: { color: 'rgba(138, 139, 148, 0.65)' },
  logoContainer: { display: 'flex', alignItems: 'center', marginLeft: 5, marginBottom: 1 },
  retailerWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '5px',
    padding: '0 40px',
    boxSizing: 'border-box',
    mt: 1,
  },
  retailerSelect: {
    color: theme.palette.grey[300],
    fontSize: 14,
  },
  appBtn: { color: '#fff', marginLeft: 'auto', marginRight: 3 },
  retailerArea: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: 2,
  },
  tenantSelect: {
    fontSize: 14,
    '& .MuiSelect-filled': {
      backgroundColor: '#fff !important',
      borderRadius: '6px !important',
    },
  },
});

interface Props {
  open: boolean;
}

export function Menu({ open }: Props) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const menuWidth = useMenuWidth();
  const { active } = useDemoContext();
  const { isProtectedRoute } = useCurrrentRoute();
  const { active: isVideoApp } = useVideoAppContext();
  const { data: meData } = useMeQuery({ enabled: Boolean(isProtectedRoute) });

  const styles = getStyles({ menuWidth, withRetailer: userService.isAdmin() || userService.ensureLevel('L1') });

  const config = useMemo(() => {
    const searchAvailable = (nested: NavItem[]): NavItem[] => {
      if (!nested) return undefined;

      return nested
        .filter(
          item => item.checkAvailability() && (item.nested !== undefined ? searchAvailable(item.nested).length : true)
        )
        .map(item => ({ ...item, nested: item.nested !== undefined ? searchAvailable(item.nested) : undefined }));
    };

    const navConfig = !isVideoApp ? getMainNavConfig(t) : getVideoNavConfig();

    return searchAvailable(navConfig);
  }, [isVideoApp, t]);

  const switchApp = () => {
    nav(`/video-redirect?type=${isVideoApp ? 'main' : 'video'}`);
  };

  return (
    <Drawer PaperProps={{ sx: styles.drawer }} anchor="left" variant="persistent" open={open}>
      <Box sx={styles.menuContainer}>
        <Box sx={styles.logoContainer}>
          <AdvonLogoIcon color="#fff" />
          {(userService.isAdmin() || Boolean(meData?.scopes?.length)) && (
            <LightTooltip title={isVideoApp ? 'Attributes App' : 'Video App'}>
              <IconButton sx={styles.appBtn} onClick={switchApp}>
                {isVideoApp ? <CategoryIcon /> : <OndemandVideoIcon />}
              </IconButton>
            </LightTooltip>
          )}
        </Box>
        {(userService.isAdmin() || userService.ensureLevel('L1') || userService.ensureLevel('L3')) && (
          <Box sx={styles.retailerWrapper}>
            {isVideoApp ? (
              <Box sx={styles.retailerArea}>
                <Typography color="white">Video App</Typography>
                <TenantSelect sx={styles.tenantSelect} fullWidth />
              </Box>
            ) : (
              <DemoBlurWrapper>
                <RetailerSelect sx={styles.retailerSelect} fullWidth demoBlur={active} />
              </DemoBlurWrapper>
            )}
          </Box>
        )}
        <Box sx={styles.navItemsWrapper}>
          <NavItems items={config} />
        </Box>
      </Box>
    </Drawer>
  );
}
